import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './UnknownLenders.css';

interface UnknownLender {
  id: string;
  name: string;
  firstSeen: string;
  lastSeen?: string;
  attempts?: number;
}

type SortField = 'name' | 'date';
type SortOrder = 'asc' | 'desc';

const UnknownLenders: React.FC = () => {
  const { apiRequest } = useAuth();
  const [rows, setRows] = useState<UnknownLender[]>([]);
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const fetchRows = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiRequest({ method: 'GET', url: '/ui/admin/unknown-lenders' }) as UnknownLender[];
      setRows(resp);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch unknown lenders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  // Sort rows based on current sort settings
  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      if (sortBy === 'name') {
        const comparison = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        return sortOrder === 'asc' ? comparison : -comparison;
      } else {
        // Sort by date (firstSeen)
        const dateA = new Date(a.firstSeen).getTime();
        const dateB = new Date(b.firstSeen).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
    return sorted;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = (field: SortField) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to asc
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleChange = (id: string, value: string) => {
    setEmailMap(prev => ({ ...prev, [id]: value }));
  };

  const saveRow = async (row: UnknownLender) => {
    const email = (emailMap[row.id] || '').trim();
    try {
      await apiRequest({
        method: 'POST',
        url: `/ui/admin/unknown-lenders/${encodeURIComponent(row.id)}`,
        data: { email },
      });
      setRows(prev => prev.filter(r => r.id !== row.id));
      const copy = { ...emailMap };
      delete copy[row.id];
      setEmailMap(copy);
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to save lender');
    }
  };

  if (loading) return <div>Loading unknown lenders…</div>;
  if (error) return <div className="error">{error}</div>;
  if (rows.length === 0) return <p>No unknown lenders 🎉</p>;

  return (
    <div className="unknown-lenders">
      {/* Sort Controls */}
      <div className="sort-controls">
        <span className="sort-label">Sort by:</span>
        <button
          className={`sort-btn ${sortBy === 'name' ? 'active' : ''}`}
          onClick={() => handleSortChange('name')}
        >
          Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A')}
        </button>
        <button
          className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
          onClick={() => handleSortChange('date')}
        >
          Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑ Oldest' : '↓ Newest')}
        </button>
      </div>

      <table className="unknown-table">
        <thead>
          <tr>
            <th
              className="sortable-header"
              onClick={() => handleSortChange('name')}
            >
              Name {sortBy === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}
            </th>
            <th
              className="sortable-header"
              onClick={() => handleSortChange('date')}
            >
              First Seen {sortBy === 'date' && (sortOrder === 'asc' ? '▲' : '▼')}
            </th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(r => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>{new Date(r.firstSeen).toLocaleDateString()}</td>
              <td>
                <input
                  type="email"
                  value={emailMap[r.id] ?? ''}
                  onChange={e => handleChange(r.id, e.target.value)}
                  placeholder="email (optional)"
                />
              </td>
              <td>
                <button className="save-btn" onClick={() => saveRow(r)}>
                  Save
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UnknownLenders;
